import { ContactFormData } from '@upright/common/activities/contact-forms/facade/contact-form';
import { getDefaultOrganization } from '@upright/common/iam/organizations/common';
import { isHomeViewOrganization } from '@upright/common/iam/organizations/home-view-organization-types';
import { useMyOrganizationsQuery } from '@upright/ui/api/iam/organizations/hooks';
import { RequestStatus } from '@upright/ui/api/common/types';
import { Modal } from '@upright/ui/common/generic/components/modal/Modal';
import { useGetLoggedInUser, useIsTeam } from '@upright/ui/common/generic/hooks';
import { organizationIdFromPath } from '@upright/ui/views/home-views/organization-id-from-path';
import { organizationNamedByUrl } from '@upright/ui/views/home-views/resolve-selected-organization';
import { ORGANIZATION_ID_SEARCH_PARAM } from '@upright/ui/views/home-views/selected-organization-context';
import { useUnifiedShell } from '@upright/ui/views/unified-shell/use-unified-shell';
import { validate } from 'email-validator';
import { isEqual, isUndefined, omitBy } from 'lodash';
import { useEffect, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { usePrevious } from '../../generic/hooks';
import { useGetTimeoutRef } from '../../generic/hooks/use-get-timeout-ref';
import { ContactForm } from './contact-form';
import { usePostContactForm } from './use-post-contact-form';

interface Props {
  site: ContactFormData['site'];
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  initialValues?: Partial<ContactFormData>;
  /** Feedback or Contact copy; by default Feedback inside the unified shell and Contact elsewhere. */
  variant?: 'feedback' | 'contact';
}

/**
 * The Contact dialog (feedback copy inside the unified shell, PLT-6962): the design system's `Modal` at the `31.25rem` short-form preset, which
 * paints the card, the padding and the close button (design/50-components.md "Dialog chrome").
 * `ContactForm` renders what goes inside it.
 */
export const ContactFormModal = (props: Props) => {
  const [fields, setFields] = useState<Partial<ContactFormData>>(props.initialValues || {});
  const [postData, postStatus, initialize] = usePostContactForm();
  const timeoutRef = useGetTimeoutRef();
  const prefill = useSignedInPrefill(props.isOpen);
  // Feedback wording only inside the unified shell, where the top bar's Feedback button opens this
  // dialog. Every other Contact entry (legacy header, footer, error pages, DMA) keeps Contact copy.
  const unifiedShell = useUnifiedShell().enabled;
  const variant = props.variant ?? (unifiedShell ? 'feedback' : 'contact');

  const previousInitialValues = usePrevious(props.initialValues);

  /*
  TODO: Instead of this useEffect hackery, provide "fields" and "setFields" as props and remove the "initialValues" prop altogether.
  A good place for "fields" and "setFields" would be a new ContactFormContext that's implemented both on the platform and on façade.
  */
  useEffect(() => {
    // If initial values don't change, we won't set them to the state. This way, the user can close and open the modal on one page
    // without resetting the values every time.
    if (props.initialValues && !isEqual(props.initialValues, previousInitialValues)) {
      setFields(props.initialValues);
    }
  }, [props.initialValues, previousInitialValues]);

  /*
   * What the form shows: the signed-in reader's own details, under anything they have typed.
   *
   * Layered at render rather than seeded into state on open, because the organisation name
   * arrives from a query — seeded, it would either miss the first open or land in the Company
   * box after the reader had started typing in it. Here a field the reader has touched holds a
   * defined value and wins, an untouched one is `undefined` and shows the prefill, and clearing
   * a field to "" keeps it cleared rather than springing back.
   */
  const shownFields: Partial<ContactFormData> = { ...prefill, ...omitBy(fields, isUndefined) };
  const canSubmit = isValidFormData(shownFields);

  return (
    <Modal
      isOpen={props.isOpen}
      onClose={() => {
        props.setIsOpen(false);
        if (postStatus === RequestStatus.SUCCESS) {
          timeoutRef.current = setTimeout(() => {
            initialize();
            setFields({});
          }, 500);
        }
      }}
      aria-label={variant === 'feedback' ? 'Give feedback' : 'Contact Upright'}
      maxInlineSize="31.25rem"
    >
      <ContactForm
        variant={variant}
        error={postStatus === RequestStatus.ERROR ? 'Could not send your message. Please try again.' : undefined}
        {...{
          fields: shownFields,
          setFields,
          validators,
          submit: () => {
            // `site` comes from the prop rather than the form: it names which product the
            // message was sent from, and the Slack notification prints it.
            const payload = { ...shownFields, site: props.site };
            if (isValidFormData(payload)) postData(payload);
          },
          postStatus,
          canSubmit,
        }}
      />
    </Modal>
  );
};

/**
 * Name, email and company for a signed-in reader, so the three things the product already knows
 * about them are not retyped into a support form. Empty on the facade, where nobody is signed in,
 * and per field: a user with no surname recorded still gets their first name.
 *
 * The organisation, for a customer, is the one they picked in the rail's selector when the URL
 * names one (`organizationNamedByUrl`, the selector's own first step), so a customer who has
 * switched organisation is not sent a form naming their first. Otherwise, and always for an
 * Upright team member, it is `getDefaultOrganization`, which is Upright for the team. A team
 * member browsing a customer's organisation is still writing as Upright, and a notification
 * naming the customer would read as if the customer had sent it. The dialog is mounted at the app
 * root, outside the selector's provider, which is why it reads the URL rather than the context.
 *
 * `isOpen` gates the organisations query: this dialog is mounted for the whole session, and a
 * support form nobody has opened is not a reason to fetch. Most callers find it already cached,
 * and a reader who does not will see Company fill a moment after the dialog opens — harmless,
 * because the prefill is layered under what they type rather than written into the field.
 */
function useSignedInPrefill(isOpen: boolean): Partial<ContactFormData> {
  const user = useGetLoggedInUser();
  const isTeam = useIsTeam();
  const [searchParams] = useSearchParams();
  const { pathname } = useLocation();
  const { data: organizations } = useMyOrganizationsQuery({ enabled: Boolean(user) && isOpen });
  const pickedOrganization = isTeam
    ? null
    : organizationNamedByUrl({
        homeViewOrganizations: organizations?.organizations.filter(isHomeViewOrganization),
        organizationIdParam: searchParams.get(ORGANIZATION_ID_SEARCH_PARAM),
        pathOrganizationId: organizationIdFromPath(pathname),
      });
  const organization = pickedOrganization ?? getDefaultOrganization(organizations?.organizations, isTeam);

  if (!user) {
    return {};
  }
  return omitBy(
    {
      name: [user.firstName, user.lastName].filter(Boolean).join(' ') || undefined,
      email: user.email,
      company: organization?.name,
    },
    isUndefined,
  );
}

function isValidFormData(data: Partial<ContactFormData>): data is ContactFormData {
  const validated = {
    name: validators.name(data.name),
    email: validators.email(data.email),
    company: validators.company(data.company),
    message: validators.message(data.message),
    site: validators.site(data.site),
    phone: validators.phone(data.phone),
  };
  const failed = Object.values(validated).filter(d => !d);
  return !failed.length;
}

const validators: {
  [key in keyof ContactFormData]: (value: ContactFormData[key] | undefined) => boolean;
} = {
  name: () => true,
  email: value => (value ? validate(value) : true),
  company: () => true,
  message: value => !!value,
  site: () => true,
  phone: () => true,
};
